import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import { fetchPricing, formatPrice, Pricing } from '../api/pricing';
import { PriceBreakdown } from './PriceBreakdown';
import { ALL_ROLES, AccountRole, ROLE_DESCRIPTIONS, ROLE_LABELS } from '../types/auth';

interface UpgradeModalProps {
  currentRole: AccountRole;
  needsRoleSelection: boolean;
  /** Epoch ms `currentRole` lapses — null until it's ever been activated. */
  roleExpiresAt: number | null;
  onClose: () => void;
}

const formatDate = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

/**
 * Popup for changing the signed-in person's platform-wide access level after
 * registration — the same choice {@code RoleSelectionModal} forces up front, just
 * reachable again later and dismissable. Reuses `selectRole`: a free switch takes
 * effect immediately, a priced one redirects to Stripe Checkout, and re-submitting
 * the role already held while payment is still pending simply resumes that checkout
 * rather than charging twice — and, once it's actually lapsed, is how renewal
 * happens too (see `RoleActivationService#selectRole`).
 */
export function UpgradeModal({ currentRole, needsRoleSelection, roleExpiresAt, onClose }: UpgradeModalProps) {
  const { selectRole } = useAuth();
  const [pricing, setPricing] = useState<Pricing | null>(null);
  const [role, setRole] = useState<AccountRole>(currentRole);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPricing().then(setPricing).catch(() => {});
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await selectRole(role);
      if (result.status !== 'active' && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not switch access levels.');
    } finally {
      setBusy(false);
    }
  }

  const price = pricing ? formatPrice(pricing.amountDueCents[role]) : null;
  const isExpired = roleExpiresAt != null && roleExpiresAt < Date.now();
  const isCurrentAndActive = role === currentRole && !needsRoleSelection;
  const isCurrentAndExpired = role === currentRole && needsRoleSelection && isExpired;
  const isCurrentAndPending = role === currentRole && needsRoleSelection && !isExpired;
  const submitLabel = isCurrentAndActive
    ? 'Current plan'
    : isCurrentAndExpired
      ? (busy ? 'Renewing…' : `Renew${price ? ` — ${price}` : ''}`)
      : isCurrentAndPending
        ? (busy ? 'Resuming…' : `Resume checkout${price ? ` — ${price}` : ''}`)
        : (busy ? 'Switching…' : `Switch${price ? ` — ${price}` : ''}`);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(4,12,24,0.92)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(96,200,232,0.28)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.55)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex items-start justify-between mb-1">
            <h2
              className="text-white font-bold text-lg"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.03em' }}
            >
              Change your access level
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-blue-300/70 hover:text-white -mt-1 -mr-1 p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="mb-5">
            <p className="text-sm mb-1" style={{ color: 'rgba(147,197,253,0.7)' }}>
              Pick the access level you would like:
            </p>
            {roleExpiresAt != null && (
              <p className={`text-xs ${isExpired ? 'text-red-300' : ''}`} style={isExpired ? undefined : { color: 'rgba(147,197,253,0.6)' }}>
                Current access {isExpired ? 'expired' : 'expires'} {formatDate(roleExpiresAt)}
              </p>
            )}
          </div>

          <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: 'rgba(147,197,253,0.7)' }}>
            Access level
          </label>
          <select
            value={role}
            onChange={e => { setRole(e.target.value as AccountRole); setError(''); }}
            className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
          >
            {ALL_ROLES.map(value => (
              <option key={value} value={value} className="bg-blue-950 text-white">
                {ROLE_LABELS[value]}
              </option>
            ))}
          </select>
          <p className="text-xs mb-1" style={{ color: 'rgba(147,197,253,0.6)' }}>
            {ROLE_DESCRIPTIONS[role]}
          </p>
          {pricing && <PriceBreakdown pricing={pricing} role={role} />}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={busy || isCurrentAndActive}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {submitLabel}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-blue-300/70 hover:text-white text-sm px-2 py-2"
            >
              Cancel
            </button>
          </div>
          {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
        </form>
      </div>
    </div>
  );
}
